import { DailyUserConsultationCount } from '@/core/api/statistics'
import { useMemo } from 'react'
import BarChartComponent from '@/components/ui/charts/BarChartComponent'
import { useGetEstablishmentConsultationRankingQuery } from '@/core/services/statistics'
import dayjs from 'dayjs'
import { Box, CircularProgress } from '@mui/material'
import NoResults from '@/assets/svg/no-results.svg?react'

export type DailyConsultationsByUserComponentProps = {
    establishmentId: string
    title: string
    startDate: Date
    endDate: Date
}

function DailyConsultationsByUserComponent(props: DailyConsultationsByUserComponentProps) {
    const { establishmentId, title, startDate, endDate } = props

    // Format dates for API
    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD')
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD')

    // Fetch data with date range filter and group by day
    const { data, isLoading, error } = useGetEstablishmentConsultationRankingQuery({
        establishment: establishmentId,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        groupByDay: true
    })

    // Transform data for the chart
    const transformedData = useMemo(() => {
        if (!data || data.length === 0) return []

        // Type guard to check if we have daily data
        const hasDailyData = data.length > 0 && 'day' in data[0]

        if (!hasDailyData) {
            return []
        }

        // Safe to cast to DailyUserConsultationCount[] once we've verified 'day' exists
        const dailyData = data as DailyUserConsultationCount[]

        // Get unique users
        const uniqueUsers = [...new Set(dailyData.map(item =>
            `${item.name} ${item.surname}`.trim()
        ))]

        // Map each user to a dataset
        return uniqueUsers.map(user => ({
            label: user,
            values: dailyData
                .filter(item => `${item.name} ${item.surname}`.trim() === user)
                .map(item => ({
                    subLabel: item.day,
                    value: item.count
                }))
        }))
    }, [data])

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '300px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Box>
        )
    }

    if (error || !data || data.length === 0) {
        return (
            <Box sx={{ minHeight: '300px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <NoResults style={{ maxWidth: '40%', height: 'auto' }} />
            </Box>
        )
    }

    return <BarChartComponent data={transformedData} title={title} />
}

export default DailyConsultationsByUserComponent; 